import React, {useCallback, useMemo, useState, useEffect, useContext, useId} from "react";
import {Hotspot} from "elements/hotspot";
import { AttentionAnimation } from 'utils/AttentionAnimationProvider';
import {MARKER, EFFECT_PREFIX} from "utils/constants";

export const CustomSurveyQuestionRenderer = (
  {
    question,
    onClick,
    containerDimensions,
    hotspotContainer,
    openHotspot,
    isHotspotSelected,
    selectedHotspotSelector,
    hotspotPopperSelector,
    snappingHotspotSelector,
    isCreativeStudioPreviewMode,
    attentionAnimationEnabled,
    inVideo,
  }
) => {
  const {
    attentionHovered, 
    setAttentionHovered, 
    attentionHoveredPreviously, 
    setAttentionHoveredPreviously, 
    attentionResetKey, 
    setAttentionResetKey
  } = useContext(AttentionAnimation);
  
  const instanceId = useId();
  const [customMarkersLoaded, setCustomMarkersLoaded] = useState(0);
  const [allCustomMarkersLoaded, setAllCustomMarkersLoaded] = useState(false);

  useEffect(() => {
    setAttentionHoveredPreviously(false)
  },[attentionAnimationEnabled, setAttentionHoveredPreviously])

  // Clean up attention hover on unmount
  useEffect(() => {
    return () => {
      setAttentionHovered(curr => (curr === instanceId ? null : curr)) 
    };
  }, [setAttentionHovered, instanceId]);

  const questionToBeUsed = useMemo(() => {
    return {
      ...question,
      hotspotText: question?.text || {},
      marker: MARKER.DEFAULT
    }
  }, [question]);

  const onQuestionClick = useCallback((question, index, isSelected, shiftKey) => {
    if (openHotspot instanceof Function) {
      openHotspot(questionToBeUsed, shiftKey);
    }
  }, [openHotspot, questionToBeUsed]);

  const isItemSelected = useCallback((item) => {
    if (isHotspotSelected instanceof Function) {
      return isHotspotSelected(item)
    }
    return false;
  }, [isHotspotSelected]);

  const isQuestionSelected = useMemo(() => {
    return isItemSelected(questionToBeUsed);
  }, [isItemSelected, questionToBeUsed]);

  const onOptionClick = useCallback((option, index, isSelected, shiftKey) => {
    if (openHotspot instanceof Function) {
      openHotspot(option, shiftKey);
    } else {
      onClick(option.id, index + 1, option.optionIndex, question.id);
    }
  }, [onClick, openHotspot, question.id]);

  const optionsCount = question.options.length;
  useEffect(() => {
    if (isCreativeStudioPreviewMode) {
      setAttentionResetKey(prevKey => prevKey + 1); 
    }
  }, [optionsCount, setAttentionResetKey, isCreativeStudioPreviewMode]);

  const attentionAnimationMap = question.options.reduce((acc, option, index) => {
    const hasEffect = option.effect && option.effect.effectType !== EFFECT_PREFIX.NONE;
    const showAttentionAnimation = hasEffect && attentionAnimationEnabled && attentionHovered === null;

    let animationIndex = null;
    if (hasEffect) {
      animationIndex = acc._count;
      acc._count++
    } 
    
    acc[index] = {
      showAttentionAnimation,
      animationIndex
    }

    if (option.marker === MARKER.CUSTOM) {
      acc._customMarkerCount++
    }

    return acc;
  }, {_count: 0, _customMarkerCount: 0})
  
  useEffect(() => {
    if (attentionAnimationMap._customMarkerCount === customMarkersLoaded) {
      setAllCustomMarkersLoaded(true);
    }
  }, [attentionAnimationMap._customMarkerCount, customMarkersLoaded]);

  return <>
    <Hotspot
      hotspot={questionToBeUsed}
      containerDimensions={containerDimensions}
      showing={Boolean(hotspotContainer)}
      useHotspotAnimation={false}
      noPointer={true}
      isCardHotspot={true}
      onClick={onQuestionClick}
      isSelected={isQuestionSelected}
      selectedHotspotSelector={selectedHotspotSelector}
      hotspotPopperSelector={hotspotPopperSelector}
      snappingHotspotSelector={snappingHotspotSelector}
      isCreativeStudioPreviewMode={isCreativeStudioPreviewMode}
    />
    {
      question.options.map((option, index) => {
        return (
          <Hotspot
            key={option.id || index}
            hotspot={{
              ...option,
              hotspotText: option?.title || {}
            }}
            onClick={onOptionClick}
            containerDimensions={containerDimensions}
            showing={Boolean(hotspotContainer)}
            useHotspotAnimation={false}
            isCardHotspot={true}
            isSurveyOption={true}
            index={index}
            isSelected={isItemSelected(option)}
            selectedHotspotSelector={selectedHotspotSelector}
            hotspotPopperSelector={hotspotPopperSelector}
            snappingHotspotSelector={snappingHotspotSelector}
            isCreativeStudioPreviewMode={isCreativeStudioPreviewMode}
            attentionAnimationEnabled={attentionAnimationEnabled}
            // OV-2427 once all custom marker images are loaded we can start the attention animation
            showAttentionAnimation={allCustomMarkersLoaded ? attentionAnimationMap[index].showAttentionAnimation : false}
            animationIndex={attentionAnimationMap[index].animationIndex}
            onMouseOver={() => {
              setAttentionHovered(instanceId);
              setAttentionHoveredPreviously(true);
            }}
            onMouseOut={() => setAttentionHovered(null)}
            resetKey={attentionResetKey}
            delayInitialAttentionAnimation={attentionHoveredPreviously}
            delayInitialAttentionAnimationForInVideo={inVideo}
            onImageLoaded={() => setCustomMarkersLoaded(prev => prev + 1)}
          />
        )
      })
    }
  </>
}
